import NewLayout from "@components/new-ui/Layout";
import Head from "next/head";
import Image from "next/image";
import { FC, FormEventHandler, useRef, useState } from "react";
import { useSnackbar } from "react-simple-snackbar";
import IntlTelInput, { CountryData } from "react-intl-tel-input";
import "react-intl-tel-input/dist/main.css";
import CTASection from "@components/new-ui/CTASection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import AwardsSection from "@components/new-ui/AwardsSection";
import { GreyCardCTASection } from "@components/solution/solutionCTASection";
import { container_classname } from "@components/new-ui/constants";
import GetStartedSection from "@components/GetStartedSection";
import { sendTrackingOnlyEmail } from "@components/WANumberForm";
import useRecaptcha from "@hooks/useRecaptcha";
import ReCAPTCHA from "react-google-recaptcha";
import React from "react";
import BlogSection from "@components/BlogSection";
import { verifyPhoneNumber } from "@utils/form";
import * as Sentry from "@sentry/nextjs";

const siteKey = process.env.NEXT_PUBLIC_API_RECAPTCHA_SITE_KEY || "";
const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ComponentType<any>;

const Contact: FC = () => {
    return (
        <NewLayout
            keywords="gallabox customer service, gallabox contact us "
            title="Gallabox | Contact Our Experts | Unlock the Power of WhatsApp"
            description="Learn all about how Gallabox can help scale your business. Need assistance? Our Customer Success experts are always available to help serve you better!"
            canonical="https://gallabox.com/contact"
        >
            <Head>
                <script src="https://fast.wistia.com/embed/medias/45yededq3y.jsonp" async />
                <script src="https://fast.wistia.com/assets/external/E-v1.js" async />
            </Head>
            <section className="pt-10 md:pt-32 md:pb-20 2xl:pt-10 relative" style={{ backgroundColor: "#EEF5FF" }}>
                <div className="max-w-4xl lg:max-w-5xl mx-auto flex flex-col items-center px-4 md:px-0">
                    <ContactForm />
                </div>
                <section className="lg:max-w-5xl mx-auto flex flex-col gap-5 lg:px-2 lg:flex-row px-3 mt-20">
                    <div className="bg-white flex-1 px-4 py-4 rounded-xl">
                        <div className="flex gap-1 items-center">
                            <h2 className="text-2xl text-gray-600 font-semibold">India</h2>
                            <Image src="/contact/India.png" width={24} height={24} alt="Indian flag" />
                        </div>
                        <p className="text-base text-gray-700 font-semibold mt-2 font-inter">Gallabox India Private Limited</p>
                        <p className="text-sm text-gray-500 mt-1 font-inter">
                            3rd Floor, &quot;Olympia Crest&quot; Plot No. 73, <br />
                            Electrical and Electronic Industries Estate, <br />
                            Perungudi, Chennai, <br />
                            Tamil Nadu – 600096
                        </p>
                    </div>
                    <div className="bg-white flex-1 px-4 py-4 rounded-xl">
                        <div className="flex gap-1 items-center">
                            <h2 className="text-2xl text-gray-600 font-semibold">UAE</h2>
                            <Image src="/contact/UAE.png" width={24} height={24} alt="UAE flag" />
                        </div>
                        <p className="text-base text-gray-700 font-semibold mt-2 font-inter">Gallabox Technology Consulting LLC</p>
                        <p className="text-sm text-gray-500 mt-1 font-inter">
                            Aspin Commercial Tower, Office 4802-029, Sheikh Zayed Road, <br />
                            Financial Center Dubai, <br />
                            Dubai, United Arab Emirates
                        </p>
                    </div>
                    <div className="bg-white flex-1 px-4 py-4 rounded-xl">
                        <div className="flex gap-1 items-center">
                            <h2 className="text-2xl text-gray-600 font-semibold">USA</h2>
                            <Image src="/contact/USA.png" width={24} height={24} alt="USA flag" />
                        </div>
                        <p className="text-base text-gray-700 font-semibold mt-2 font-inter">Gallabox Global Inc</p>
                        <p className="text-sm text-gray-500 mt-1 font-inter">
                            850 New Burton Road,Suite 201, <br className="lg:block hidden" />
                            City of Dover, Kent, DE 19904
                        </p>
                    </div>
                </section>
            </section>

            <GetStartedSection variant="blue" />

            <section className={`my-20 px-5 ${container_classname}  `}>
                <h2 className="text-6xl mb-10" style={{ fontFamily: "Degular Display" }}>
                    Customer story
                </h2>
                <GreyCardCTASection urlLink={"https://gallabox.com/case-study/numberwale"} />
            </section>
            <BlogSection />
            <IntegrationSection />
            <AwardsSection />
            <CTASection title="Turn connections into conversions" description="Be among the first to ride the WhatsApp automation wave" />

            <style jsx>{`
                .blog-rm:hover {
                    box-shadow: 0px 12px 24px 1px rgba(51, 100, 250, 0.21);
                }

                .blog-rm:hover .blog-rm-icon {
                    transform: translateX(0.25rem);
                }
            `}</style>
        </NewLayout>
    );
};

export default Contact;

const ContactForm: FC = () => {
    const [isLoading, setLoading] = useState<boolean>(false);
    const [customerType, setCustomerType] = useState<"existing" | "new">("new");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [countryCode, setCountryCode] = useState<string>("+91");

    const [openSuccessSnackbar] = useSnackbar({ style: { backgroundColor: "#10B981" } });
    const [openErrorSnackbar] = useSnackbar({ style: { backgroundColor: "#DC2626" } });

    const formRef = useRef<HTMLFormElement>(null);
    const pendingFormValuesRef = useRef<Record<string, string>>({});

    const submitWithToken = async (token: string) => {
        const formValues = pendingFormValuesRef.current;
        const phone = formValues.phoneNumber;
        try {
            await sendTrackingOnlyEmail({
                email: formValues.email || "",
                name: customerType === "new" ? formValues.name || "" : "",
                mobile: customerType === "new" ? phone || "" : "",
                attributes: {
                    message: formValues.message || "",
                    customer_type: customerType,
                },
                subjectSuffix: customerType === "existing" ? "Contact Submit - Existing" : "Contact Submit - New",
                ctaLabel: customerType === "existing" ? "Contact Form Submit - Existing Customer" : "Contact Form Submit - New Customer",
                recaptchaToken: token,
            });
            openSuccessSnackbar("Your message is received! We will get back to you soon.", 5000);
            formRef.current?.reset();
            setPhoneNumber("");
        } catch (error) {
            Sentry.captureException(error);
            const errorMessage = (error as any).response?.data?.error;
            openErrorSnackbar(errorMessage || "OOPS! Something went wrong, please send your message to team@gallabox.com", 10000);
        } finally {
            setLoading(false);
        }
    };

    const { reCaptchaRef, triggerReCaptcha, generateToken } = useRecaptcha(submitWithToken, () => {
        openErrorSnackbar("reCAPTCHA verification failed. Please try again.", 5000);
        setLoading(false);
    });

    const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();

        setLoading(true);

        const formElements = Array.from(e.currentTarget.elements) as HTMLInputElement[];
        const formValues = formElements.reduce((acc, el) => {
            if (el.name) {
                if (el.type === "radio") {
                    if (el.checked) {
                        acc[el.name] = el.value;
                    }
                } else {
                    acc[el.name] = String(el.value);
                }
            }
            return acc;
        }, {} as Record<string, string>);

        pendingFormValuesRef.current = { ...formValues, phoneNumber: countryCode + phoneNumber };
        triggerReCaptcha();
    };

    return (
        <form ref={formRef} className="pt-10 pb-5 z-1 w-full lg:px-3" onSubmit={onSubmit}>
            <div className="rounded-2xl w-full overflow-hidden shadow-lg">
                <div className="py-6 px-6 md:px-8 text-white" style={{ backgroundColor: "#2A4365" }}>
                    <h2 className="m-0 p-0 text-3xl font-semibold tracking-wide">Contact Us</h2>
                    <p className="m-0 mt-1 text-base opacity-90">Any question or remarks? Just write us a message!</p>
                </div>

                <div className="py-8 px-6 md:px-8 " style={{ backgroundColor: "#DDEBFF" }}>
                    <div className="mb-6">
                        <p className="text-gray-700 font-medium mb-3">Are you an existing customer?</p>
                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="radio"
                                    name="customerType"
                                    value="existing"
                                    checked={customerType === "existing"}
                                    onChange={() => setCustomerType("existing")}
                                    className="hidden"
                                />
                                <span
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        customerType === "existing" ? "border-crayola-blue bg-crayola-blue" : "border-gray-300 bg-white"
                                    }`}
                                >
                                    {customerType === "existing" && <span className="w-2 h-2 bg-white rounded-full"></span>}
                                </span>
                                <span className="text-gray-700">Existing customer?</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="radio" name="customerType" value="new" checked={customerType === "new"} onChange={() => setCustomerType("new")} className="hidden" />
                                <span
                                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                        customerType === "new" ? "border-crayola-blue bg-crayola-blue" : "border-gray-300 bg-white"
                                    }`}
                                >
                                    {customerType === "new" && <span className="w-2 h-2 bg-white rounded-full"></span>}
                                </span>
                                <span className="text-gray-700">New</span>
                            </label>
                        </div>
                    </div>

                    {customerType === "new" && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="flex flex-col">
                                    <label htmlFor="name" className="text-gray-700 font-medium mb-2">
                                        Name
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                                        required
                                        id="name"
                                        name="name"
                                        type="text"
                                        placeholder="Your Name"
                                        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label htmlFor="email" className="text-gray-700 font-medium mb-2">
                                        Email
                                    </label>
                                    <input
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                                        required
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="Your E-Mail ID"
                                        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label htmlFor="phoneNumber" className="text-gray-700 font-medium mb-2 block">
                                    Phone Number
                                </label>
                                <IntlTelInput
                                    containerClassName="intl-tel-input w-full"
                                    inputClassName="w-full  rounded-lg border border-gray-200 bg-white placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                                    defaultCountry="in"
                                    value={phoneNumber}
                                    telInputProps={{ style: { height: "3rem" } as React.CSSProperties }}
                                    onSelectFlag={(e: string, countryData: CountryData) => {
                                        setCountryCode(countryData?.dialCode ? "+" + countryData.dialCode : "+91");
                                    }}
                                    onPhoneNumberChange={(_isValid: boolean, value: string) => {
                                        if (verifyPhoneNumber(value)) {
                                            setPhoneNumber(value);
                                        }
                                    }}
                                    placeholder="Your Phone Number"
                                />
                            </div>
                        </>
                    )}

                    {customerType === "existing" && (
                        <div className="flex flex-col">
                            <label htmlFor="email" className="text-gray-700 font-medium mb-2">
                                Registered E-Mail ID
                            </label>
                            <input
                                className="w-full px-4 py-3 rounded-lg border border-gray-200  placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent"
                                required
                                id="email"
                                name="email"
                                type="email"
                                placeholder="Your Registered E-Mail ID"
                                style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
                            />
                        </div>
                    )}
                    <div className="mb-6">
                        <label htmlFor="message" className="text-gray-700 font-medium mb-2 block">
                            {customerType === "existing" ? "Tell us about your experience with us" : "Tell us about your current setup, goals or support you're looking for"}
                        </label>
                        <textarea
                            className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white placeholder-gray-400 text-gray-700 focus:outline-none focus:ring-2 focus:ring-crayola-blue focus:border-transparent resize-none"
                            id="message"
                            name="message"
                            rows={5}
                            required
                        />
                    </div>

                    <div className="flex justify-end">
                        <button
                            disabled={isLoading}
                            className="track_contactussubmit outline-none focus:outline-none submit-btn whitespace-nowrap bg-crayola-blue hover:bg-blue-700 hover:shadow-md text-white rounded-lg px-8 py-3 inline-flex items-center justify-center gap-2 transition duration-300 ease-in-out"
                            type="submit"
                        >
                            {isLoading ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                            ) : (
                                <span className="text-sm tracking-wider font-medium">Submit</span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            {siteKey && <ReCAPTCHAComponent ref={reCaptchaRef} sitekey={siteKey} size="invisible" onChange={generateToken} />}
        </form>
    );
};
